import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, X, AlertTriangle, AlertCircle, Info, ChevronRight, Sparkles } from "lucide-react";

const SEV = {
 danger: { icon: AlertTriangle, accent: "border-l-[#B35D4C]", text: "text-[#B35D4C]" },
 warn: { icon: AlertCircle, accent: "border-l-[#D4A24C]", text: "text-[#8A6D3B]" },
 info: { icon: Info, accent: "border-l-[#1B6C62]", text: "text-[#1B6C62]" },
};

/**
 * NotificationBell. sits in the Navbar (only when authed). Polls
 * /api/insights/all every 90s. Dot turns red if anything is danger/warn.
 * Click → dropdown listing all open insights across all cases, grouped by case.
 * Each row: "Address" → navigates to /cases/:id?tab=<action.target> and the
 * CaseDetail page picks up `?tab=…` from the URL to switch tabs.
 */
export default function NotificationBell() {
 const [items, setItems] = useState([]);
 const [open, setOpen] = useState(false);
 const wrapperRef = useRef(null);
 const navigate = useNavigate();

 const load = async () => {
 try {
 const { data } = await api.get(`/insights/all`);
 setItems(data.insights || []);
 } catch (e) {
 // user might not be authed (Navbar polls everywhere); silent
 setItems([]);
 }
 };

 useEffect(() => {
 load();
 const t = setInterval(load, 90000);
 return () => clearInterval(t);
 }, []);

 useEffect(() => {
 const handler = (e) => {
 if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
 };
 if (open) document.addEventListener("mousedown", handler);
 return () => document.removeEventListener("mousedown", handler);
 }, [open]);

 const count = items.length;
 const hasUrgent = items.some(i => i.severity === "danger" || i.severity === "warn");

 const dismiss = async (it, e) => {
 e?.stopPropagation();
 setItems(cs => cs.filter(c => c.id !== it.id));
 try { await api.post(`/insights/dismiss`, { insight_id: it.id, case_id: it.case_id }); }
 catch (_) { /* optimistic UI: already removed locally, server retry isn't worth a toast */ }
 };

 const goAddress = (it) => {
 setOpen(false);
 const target = it.action?.target;
 if (it.action?.kind === "open_tab" && it.case_id && target) {
 navigate(`/cases/${it.case_id}?tab=${encodeURIComponent(target)}`);
 } else if (it.action?.kind === "open_document" && it.case_id) {
 navigate(`/cases/${it.case_id}?tab=documents&doc=${encodeURIComponent(target)}`);
 } else if (it.case_id) {
 navigate(`/cases/${it.case_id}`);
 }
 };

 // Group by case
 const byCase = items.reduce((acc, i) => {
 const k = i.case_id;
 (acc[k] = acc[k] || { case_address: i.case_address, items: [] }).items.push(i);
 return acc;
 }, {});

 return (
 <div ref={wrapperRef} className="relative">
 <button
 onClick={() => setOpen(o => !o)}
 className="relative tap-target hover:bg-[#FAF9F6] transition-colors"
 title="Smart insights"
 data-testid="notification-bell"
 >
 <Bell size={20} strokeWidth={1.5} className="text-[#0D2B52]" />
 {count > 0 && (
 <span
 className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 ${hasUrgent ? "bg-[#B35D4C]" : "bg-[#1B6C62]"} text-white text-[9px] font-mono font-bold rounded-full flex items-center justify-center`}
 data-testid="notification-count"
 >
 {count > 9 ? "9+" : count}
 </span>
 )}
 </button>

 {open && (
 <div className="absolute right-0 mt-1 w-96 max-h-[480px] overflow-y-auto bg-white border border-[#E5E4DE] shadow-2xl z-50" data-testid="notification-panel">
 <div className="px-4 py-3 border-b border-[#E5E4DE] flex items-center gap-2 sticky top-0 bg-white">
 <Sparkles size={14} strokeWidth={1.75} className="text-[#1B6C62]" />
 <span className="label-mono">Smart insights</span>
 {count > 0 && <span className="ml-auto text-[11px] text-[#6B7280] font-mono">{count} open</span>}
 </div>

 {count === 0 ? (
 <div className="px-4 py-10 text-center">
 <div className="text-3xl mb-2">·</div>
 <p className="text-sm text-[#0D2B52] font-serif">All clear.</p>
 <p className="text-xs text-[#6B7280] mt-1">We'll nudge you when something needs attention.</p>
 </div>
 ) : (
 Object.entries(byCase).map(([cid, group]) => (
 <div key={cid} className="border-b border-[#E5E4DE]">
 <div className="px-4 pt-3 pb-1 label-mono text-[#0D2B52]">{group.case_address}</div>
 {group.items.map(it => {
 const cfg = SEV[it.severity] || SEV.info;
 const Icon = cfg.icon;
 return (
 <div key={it.id}
 onClick={() => it.action && goAddress(it)}
 className={`px-4 py-3 hover:bg-[#FAF9F6] cursor-pointer border-l-4 ${cfg.accent} flex gap-2.5`}
 data-testid={`notif-item-${it.id}`}>
 <Icon size={14} strokeWidth={1.75} className={`${cfg.text} mt-0.5 flex-shrink-0`} />
 <div className="flex-1 min-w-0">
 <div className="text-[13px] font-serif text-[#0D2B52] leading-snug">{it.title}</div>
 <p className="text-[12px] text-[#6B7280] line-clamp-2 leading-snug mt-0.5">{it.body}</p>
 {it.action && (
 <div className="mt-1 text-[11px] text-[#1B6C62] inline-flex items-center gap-0.5 font-medium">
 {it.action.label} <ChevronRight size={10} strokeWidth={2} />
 </div>
 )}
 </div>
 <button onClick={(e) => dismiss(it, e)}
 className="self-start p-1 text-[#6B7280] hover:text-[#0D2B52]"
 title="Dismiss"
 data-testid={`notif-dismiss-${it.id}`}>
 <X size={12} strokeWidth={1.5} />
 </button>
 </div>
 );
 })}
 </div>
 ))
 )}
 </div>
 )}
 </div>
 );
}
